import { LockOutlined } from "@ant-design/icons";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { BaseModal } from "../CustomAntdComponents/BaseModal";
import { Button } from "../CustomAntdComponents/Button";
import {
  trackPaywallUpgradeClicked,
  trackPaywallViewed,
} from "../../../analytics/productEvents";
import { Paths } from "../../../router/paths";
import { useShouldHideSubscriptions } from "../../../hooks/useShouldHideSubscriptions";
import { useTrialOffer } from "../../../hooks/useTrialOffer";
import styles from "./UpgradeLimitModal.module.css";

interface UpgradeLimitModalProps {
  open: boolean;
  onClose: () => void;
  featureKey?: string;
  category?: string;
}

export const UpgradeLimitModal: React.FC<UpgradeLimitModalProps> = ({
  open,
  onClose,
  featureKey,
  category,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { symbol } = useParams<{ symbol?: string }>();
  const hideSubscriptions = useShouldHideSubscriptions();
  // An eligible Free user is offered the trial instead of an upgrade (docs/free-trial-design.md).
  const offerTrial = useTrialOffer().isEligible;

  useEffect(() => {
    if (!open) return;
    trackPaywallViewed({
      feature_key: featureKey,
      symbol,
      category,
      surface: window.location.pathname,
      trial_offer: offerTrial,
    });
  }, [open, featureKey, symbol, category, offerTrial]);

  const handleUpgradeClick = () => {
    trackPaywallUpgradeClicked({
      feature_key: featureKey,
      symbol,
      category,
      destination: Paths.SUBSCRIPTIONS,
      trial_offer: offerTrial,
    });
    onClose();
    navigate(Paths.SUBSCRIPTIONS);
  };

  return (
    <BaseModal
      open={open}
      onCancel={onClose}
      width={400}
      title={
        offerTrial
          ? t("pages.lockDisplay.trialPrompt")
          : t("pages.lockDisplay.upgradePrompt")
      }
      icon={<LockOutlined className={styles.icon} />}
      iconWithBorder
      footer={
        <>
          <Button onClick={onClose} className={styles["cancel-button"]}>
            {t("pages.lockDisplay.cancel")}
          </Button>
          {!hideSubscriptions && (
            <Button
              type="primary"
              onClick={handleUpgradeClick}
              className={styles["upgrade-button"]}
            >
              {offerTrial
                ? t("pages.lockDisplay.trialButton")
                : t("pages.lockDisplay.upgradeButton")}
            </Button>
          )}
        </>
      }
    >
      <div />
    </BaseModal>
  );
};
